// mon-budget-app/src/pages/DebtCreditPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useSelected } from "../contexts/SelectedContext";
import DebtCreditRow from "../components/DebtCreditRow";
import ReimbursementModal from "../components/ReimbursementModal";
import FactureModal from '../components/FactureModal';

// Import des Hooks et Constantes de Thème
import { useTheme } from "../contexts/ThemeContext";
import { THEMES, THEME_COLORS } from "../components/Constants";
import { authFetch } from "../utils/api";

const formatCurrency = (amount) => {
    return Number(amount).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    const cleanDateString = dateString.replace(' ', 'T').split('T')[0];
    const date = new Date(cleanDateString);
    if (isNaN(date)) {
        console.error("Erreur de formatage de date:", dateString);
        return 'Invalid Date';
    }
    return date.toLocaleDateString("fr-FR");
};

// Styles de base NEUTRES (les couleurs seront écrasées dynamiquement)
const DEBT_CREDIT_STYLES = {
    container: { padding: '20px', maxWidth: '1200px', margin: 'auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '10px' },
    personSelector: { padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', marginRight: '10px' },
    summaryBox: { padding: '15px', borderRadius: '8px', display: 'flex', justifyContent: 'space-around', margin: '20px 0' },
    summaryItem: { textAlign: 'center' },
    section: { marginBottom: '30px' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px' },
    th: { padding: '10px', textAlign: 'left' },
    thCenter: { padding: '10px', textAlign: 'center' },
    h2: { marginTop: '0' },
    button: { padding: '8px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', marginLeft: '10px', fontWeight: 'bold' },
    emptyMessage: { textAlign: 'center', padding: '20px', border: '1px solid #ccc', borderRadius: '4px', marginTop: '10px' },
};

export default function DebtCreditPage({ currentUser, persons, accounts, categories, refreshAllData }) {
    // Récupération du thème
    const { currentTheme } = useTheme();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

    const { 
        selectedPerson: globalSelectedPerson, 
        selectedAccount: globalSelectedAccount
    } = useSelected();

    // États existants
    const [focusedPersonId, setFocusedPersonId] = useState(globalSelectedPerson?.id || -1);
    const [debts, setDebts] = useState([]);
    const [credits, setCredits] = useState([]);
    const [selectedDebtCreditIds, setSelectedDebtCreditIds] = useState([]);
    const [showReimbursementModal, setShowReimbursementModal] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    // Nouveaux états pour les onglets Historique
    const [activeTab, setActiveTab] = useState('active');
    const [debtsHistory, setDebtsHistory] = useState([]);
    const [creditsHistory, setCreditsHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Nouveaux états pour la FactureModal
    const [showFactureModal, setShowFactureModal] = useState(false);
    const [editingFacture, setEditingFacture] = useState(null); // Facture à afficher

    // Mapping dynamique des couleurs de statut
    const statusColors = {
        'Pending': themeColors.danger,
        'Partially Paid': themeColors.accent, // Utilisation de l'accent (souvent orange/jaune)
        'Settled': themeColors.success
    };

    // Initialisation du focusedPersonId
    useEffect(() => {
        if (globalSelectedPerson && focusedPersonId === -1) {
            setFocusedPersonId(globalSelectedPerson.id);
        }
    }, [globalSelectedPerson, focusedPersonId]);

    // --- Fonctions de récupération de données (Inchangées) ---
    const fetchDebtsCredits = useCallback(async () => {
        const currentFocusId = focusedPersonId;
        if (!currentFocusId || currentFocusId === -1 || !currentUser) return;
        setIsLoading(true);
        try {
            const response = await authFetch.get(`/debts-credits/${currentUser.id}/${currentFocusId}`);
            if (response.ok) {
                const data = await response.json();
                setDebts(data.debts || []);
                setCredits(data.credits || []);
            } else {
                console.error("Erreur de chargement des dettes/créances actives");
                setDebts([]);
                setCredits([]);
            }
        } catch (error) {
            console.error("Erreur fetch debts/credits actives:", error);
            setDebts([]);
            setCredits([]);
        } finally {
            setIsLoading(false);
        }
    }, [focusedPersonId, currentUser]);

    const fetchDebtHistory = useCallback(async () => {
        const currentFocusId = focusedPersonId;
        if (!currentFocusId || currentFocusId === -1 || !currentUser) return;

        setIsLoading(true);
        try {
            const response = await authFetch.get(`/debts/history/${currentFocusId}`);
            if (response.ok) {
                const data = await response.json();
                setDebtsHistory(data);
            } else {
                console.error("Erreur de chargement de l'historique des dettes");
                setDebtsHistory([]);
            }
        } catch (error) {
            console.error("Erreur fetch debt history:", error);
            setDebtsHistory([]);
        } finally {
            setIsLoading(false);
        }
    }, [focusedPersonId, currentUser]);

    const fetchCreditHistory = useCallback(async () => {
        const currentFocusId = focusedPersonId;
        if (!currentFocusId || currentFocusId === -1 || !currentUser) return;

        setIsLoading(true);
        try {
            // 🔑 CORRECTION 3 : GET /credits/history/:personId
            const response = await authFetch.get(`/credits/history/${currentFocusId}`);
            if (response.ok) {
                const data = await response.json();
                setCreditsHistory(data);
            } else {
                console.error("Erreur de chargement de l'historique des créances");
                setCreditsHistory([]);
            }
        } catch (error) {
            console.error("Erreur fetch credit history:", error);
            setCreditsHistory([]);
        } finally {
            setIsLoading(false);
        }
    }, [focusedPersonId, currentUser]);


    // Effet de chargement des données (Inchangé)
    useEffect(() => {
        if (focusedPersonId && focusedPersonId !== -1) {
            if (activeTab === 'active') {
                fetchDebtsCredits();
            } else if (activeTab === 'debtHistory') {
                fetchDebtHistory();
            } else if (activeTab === 'creditHistory') {
                fetchCreditHistory();
            }
        }
    }, [fetchDebtsCredits, fetchDebtHistory, fetchCreditHistory, refreshKey, activeTab, focusedPersonId]);

    // 💡 FONCTION POUR CHARGER ET OUVRIR LA FACTURE
    const handleViewFacture = useCallback(async (factureId) => {
        if (!factureId) return;

        try {
            // 1. Charger les données complètes de la facture d'origine
            const response = await authFetch.get(`/factures/${factureId}`);
            
            if (response.ok) {
                const factureData = await response.json();
                
                setEditingFacture(factureData); 
                setShowFactureModal(true); // Ouvre le modal
            } else {
                throw new Error("Échec du chargement de la facture.");
            }

        } catch (error) {
            console.error("❌ Échec du chargement de la facture:", error);
            // Utiliser notify ou alert ici
        }
    }, [setEditingFacture]);

    // Totaux et utilitaires (Inchangé)
    const totalDebts = debts.reduce((sum, d) => sum + Number(d.remaining_amount || 0), 0);
    const totalCredits = credits.reduce((sum, c) => sum + Number(c.remaining_amount || 0), 0);
    const netBalance = totalCredits - totalDebts;
    const personFocusObject = persons.find(p => p.id === Number(focusedPersonId));
    const selectedPersonName = personFocusObject?.name || "N/A";

    // Gestion de la sélection et du modal (Inchangé)
    const handleSelectDebtCredit = (id) => {
        setSelectedDebtCreditIds(prev =>
            prev.includes(id) ? prev.filter(dcId => dcId !== id) : [...prev, id]
        );
    };

    const handleOpenReimbursementModal = () => {
        const selectedActiveItems = selectedDebtCreditIds
            .map(id => [...debts, ...credits].find(item => item.id === id))
            .filter(item => item && item.remaining_amount > 0.01);

        if (selectedActiveItems.length === 0) {
            alert("Veuillez sélectionner au moins une dette/créance active à rembourser.");
            return;
        }

        setShowReimbursementModal(true);
    };

    const handleCloseReimbursementModal = () => {
        setShowReimbursementModal(false);
        setSelectedDebtCreditIds([]);
        setRefreshKey(prev => prev + 1);

        if (refreshAllData) refreshAllData();
    };

    const selectedItems = [...debts, ...credits].filter(item => selectedDebtCreditIds.includes(item.id));
    const totalNetSelectedAmount = selectedItems.reduce((netSum, item) => {
        const amount = Number(item.remaining_amount || 0);
        const isCredit = credits.some(c => c.id === item.id);
        return netSum + (isCredit ? amount : -amount);
    }, 0);
    const totalAmountToReimburse = totalNetSelectedAmount;

    // --- Styles Dynamiques ---
    // Fonction qui retourne un style pour la couleur nette, basée sur le thème
    const totalNetStyle = (amount) => ({
        color: amount >= 0 ? themeColors.success : themeColors.danger,
        fontWeight: 'bold'
    });

    // --- Composant Historique (Interne) ---
    const HistoryTable = ({ data, type }) => {
        if (isLoading) return <p style={{ textAlign: 'center', padding: '20px', color: themeColors.textTerciary }}>Chargement de l'historique...</p>;
        if (data.length === 0) return <p style={{ ...DEBT_CREDIT_STYLES.emptyMessage, color: themeColors.textTerciary, border: `1px solid ${themeColors.inputBorder}` }}>Aucun historique trouvé pour les {type}.</p>;

        const isDebt = type === 'dettes';
        const title = isDebt ? "Créancier (Payeur Initial)" : "Débiteur (Bénéficiaire)";

        return (
            <table style={{ ...DEBT_CREDIT_STYLES.table, backgroundColor: themeColors.mainBackground }}>
                <thead>
                    <tr>
                        {[['Facture (Description)', 'left'], [title, 'left'], ['Date Remb.', 'center'], ['Mode Paiement', 'center'], ['Montant Remb.', 'center']].map(([header, align], index) => (
                            <th
                                key={index}
                                style={{
                                    ...DEBT_CREDIT_STYLES.th,
                                    borderBottom: `2px solid ${themeColors.secondary}`,
                                    textAlign: align,
                                    backgroundColor: themeColors.navActive
                                }}
                            >
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, index) => {
                        
                        // 💡 NOUVELLES DONNÉES DU BE
                        const reimbursementAmount = Number(item.montant || 0);
                        const reimbursementDate = formatDateForDisplay(item.date);
                        const paymentMode = item.mode_paiement || 'N/A';
                        
                        return (
                            // 💡 AJOUT DU CLIC POUR OUVRIR LA MODALE
                            <tr 
                                key={item.debt_credit_id || index} 
                                // 💡 Utilise le nouveau champ facture_id
                                onClick={() => handleViewFacture(item.facture_id)} 
                                style={{ 
                                    cursor: 'pointer', 
                                    backgroundColor: index % 2 === 0 ? themeColors.mainBackground : themeColors.formBackground, 
                                    color: themeColors.text 
                                }}
                            >
                                <td style={{ ...DEBT_CREDIT_STYLES.th, borderBottom: `1px solid ${themeColors.inputBorder}` }}>
                                    {item.facture_description}
                                </td>
                                <td style={{ ...DEBT_CREDIT_STYLES.th, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{item.partner_name}</td>
                                <td style={{ ...DEBT_CREDIT_STYLES.thCenter, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{reimbursementDate}</td>
                                <td style={{ ...DEBT_CREDIT_STYLES.thCenter, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{paymentMode}</td>
                                <td style={{ ...DEBT_CREDIT_STYLES.thCenter, borderBottom: `1px solid ${themeColors.inputBorder}`, fontWeight: 'bold' }}>
                                    {formatCurrency(reimbursementAmount)} €
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <div style={DEBT_CREDIT_STYLES.container}>
            <div style={{ ...DEBT_CREDIT_STYLES.header, borderBottom: `2px solid ${themeColors.inputBorder}` }}>
                <h1 style={{ ...DEBT_CREDIT_STYLES.h2, color: themeColors.title }}>Gestion des Dettes</h1>
                <div>
                    <label htmlFor="person-focus-select" style={{ color: themeColors.textTerciary, fontWeight: '600'}}>Personne focus : </label>
                    <select
                        id="person-focus-select"
                        value={focusedPersonId === -1 ? '' : focusedPersonId || ''}
                        onChange={(e) => setFocusedPersonId(Number(e.target.value))}
                        style={{ ...DEBT_CREDIT_STYLES.personSelector, border: `1px solid ${themeColors.inputBorder}`, color: themeColors.text, backgroundColor: themeColors.inputBackground, fontWeight: 'bold'}}
                    >
                        <option value="">-- Sélectionner une personne --</option>
                        {persons.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {(focusedPersonId && focusedPersonId !== -1) ? (
                <>
                    {/* Onglets de navigation */}
                    <div style={{ display: 'flex', borderBottom: `2px solid ${themeColors.inputBorder}`, marginBottom: '15px' }}>
                        {['active', 'debtHistory', 'creditHistory'].map(tab => {
                            const tabName = tab === 'active' ? 'Actif' : tab === 'debtHistory' ? 'Historique Dettes' : 'Historique Créances';
                            const isActive = activeTab === tab;
                            return (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    style={{
                                        padding: '10px 15px',
                                        cursor: 'pointer',
                                        border: 'none',
                                        borderBottom: isActive ? `3px solid ${themeColors.secondary}` : 'none',
                                        backgroundColor: isActive ? themeColors.primary : 'transparent',
                                        fontWeight: "bold",
                                        marginRight: '5px',
                                        color: themeColors.title
                                    }}
                                >
                                    {tabName}
                                </button>
                            );
                        })}
                    </div>

                    {/* -------------------- Contenu de l'onglet ACTIF -------------------- */}
                    {activeTab === 'active' && (
                        <>
                            {/* Sommaire */}
                            <div style={{ ...DEBT_CREDIT_STYLES.summaryBox, backgroundColor: themeColors.mainBackground, color: themeColors.text, border: `1px solid ${themeColors.inputBorder}` }}>
                                <div style={DEBT_CREDIT_STYLES.summaryItem}>
                                    Dettes actives : <span style={{ color: themeColors.danger, fontWeight: 'bold' }}>{formatCurrency(totalDebts)} €</span>
                                </div>
                                <div style={DEBT_CREDIT_STYLES.summaryItem}>
                                    Créances actives : <span style={{ color: themeColors.success, fontWeight: 'bold' }}>{formatCurrency(totalCredits)} €</span>
                                </div>
                                <div style={DEBT_CREDIT_STYLES.summaryItem}>
                                    Solde Net : <span style={totalNetStyle(netBalance)}>{formatCurrency(netBalance)} €</span>
                                </div>
                            </div>

                            {/* Action Groupée */}
                            <div style={{ marginBottom: '20px', textAlign: 'right' }}>
                                <button
                                    style={{ ...DEBT_CREDIT_STYLES.button, backgroundColor: themeColors.primary, color: themeColors.textSecondary }}
                                    onClick={handleOpenReimbursementModal}
                                    disabled={selectedItems.length === 0}
                                >
                                    Rembourser la sélection ({selectedItems.length} lignes)
                                </button>
                            </div>

                            {/* Sections Dettes/Créances Actives */}
                            <div style={DEBT_CREDIT_STYLES.section}>
                                <h2 style={{ color: themeColors.title, ...DEBT_CREDIT_STYLES.h2 }}>Créances</h2>
                                {isLoading ? (<p style={{ ...DEBT_CREDIT_STYLES.emptyMessage, color: themeColors.textTerciary, border: `1px solid ${themeColors.inputBorder}` }}>Chargement des créances actives...</p>) : credits.length === 0 ? (
                                    <p style={{ ...DEBT_CREDIT_STYLES.emptyMessage, color: themeColors.textTerciary, border: `1px solid ${themeColors.inputBorder}` }}>Aucune créance active enregistrée pour {selectedPersonName}.</p>
                                ) : (
                                    <table style={{ ...DEBT_CREDIT_STYLES.table, backgroundColor: themeColors.mainBackground }}>
                                        <thead><DebtCreditTableHeader themeColors={themeColors} /></thead>
                                        <tbody>
                                            {credits.map(c => (
                                                <DebtCreditRow
                                                    key={c.id}
                                                    item={c}
                                                    type="credit"
                                                    isSelected={selectedDebtCreditIds.includes(c.id)}
                                                    onSelect={handleSelectDebtCredit}
                                                    styles={DEBT_CREDIT_STYLES}
                                                    persons={persons}
                                                    themeColors={themeColors}
                                                    statusColors={statusColors}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div style={DEBT_CREDIT_STYLES.section}>
                                <h2 style={{ color: themeColors.title, ...DEBT_CREDIT_STYLES.h2 }}>Dettes</h2>
                                {isLoading ? (<p style={{ ...DEBT_CREDIT_STYLES.emptyMessage, color: themeColors.textTerciary, border: `1px solid ${themeColors.inputBorder}` }}>Chargement des dettes actives...</p>) : debts.length === 0 ? (
                                    <p style={{ ...DEBT_CREDIT_STYLES.emptyMessage, color: themeColors.textTerciary, border: `1px solid ${themeColors.inputBorder}` }}>Aucune dette active enregistrée pour {selectedPersonName}.</p>
                                ) : (
                                    <table style={{ ...DEBT_CREDIT_STYLES.table, backgroundColor: themeColors.mainBackground }}>
                                        <thead><DebtCreditTableHeader themeColors={themeColors} /></thead>
                                        <tbody>
                                            {debts.map(d => (
                                                <DebtCreditRow
                                                    key={d.id}
                                                    item={d}
                                                    type="debt"
                                                    isSelected={selectedDebtCreditIds.includes(d.id)}
                                                    onSelect={handleSelectDebtCredit}
                                                    styles={DEBT_CREDIT_STYLES}
                                                    persons={persons}
                                                    themeColors={themeColors}
                                                    statusColors={statusColors}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div style={{ marginTop: '15px', padding: '10px', borderTop: `2px solid ${themeColors.textTerciary}` }}>
                                {/* (Affichage du solde net sélectionné) */}
                                {selectedItems.length > 0 ? (
                                    <p style={{ color: themeColors.textTerciary }}>
                                        **Solde net à régler pour la sélection :** <span style={{ fontSize: '1.2em', fontWeight: 'bold', ...totalNetStyle(totalAmountToReimburse) }}>
                                            {formatCurrency(Math.abs(totalAmountToReimburse))} €
                                        </span> ({selectedItems.length} lignes)
                                    </p>
                                ) : (<p style={{ color: themeColors.textTerciary }}>Sélectionnez une ou plusieurs lignes pour voir le solde net à régler.</p>)}
                            </div>
                        </>
                    )}

                    {/* -------------------- Contenu de l'onglet Historique Dettes -------------------- */}
                    {activeTab === 'debtHistory' && (
                        <div style={DEBT_CREDIT_STYLES.section}>
                            <h2 style={{ color: themeColors.title, ...DEBT_CREDIT_STYLES.h2 }}>Historique des Dettes ({selectedPersonName} a remboursé)</h2>
                            <HistoryTable data={debtsHistory} type="dettes" />
                        </div>
                    )}

                    {/* -------------------- Contenu de l'onglet Historique Créances -------------------- */}
                    {activeTab === 'creditHistory' && (
                        <div style={DEBT_CREDIT_STYLES.section}>
                            <h2 style={{ color: themeColors.title, ...DEBT_CREDIT_STYLES.h2 }}>Historique des Créances (Remboursé à {selectedPersonName})</h2>
                            <HistoryTable data={creditsHistory} type="créances" />
                        </div>
                    )}

                    {/* 💡 MODAL DE FACTURE (pour l'historique) */}
                    {showFactureModal && (
                        <FactureModal
                            show={showFactureModal}
                            editingFacture={editingFacture} // Contient la facture chargée
                            currentUser={currentUser}
                            categories={categories}
                            accounts={accounts}
                            persons={persons}
                            selectedPerson={globalSelectedPerson}
                            selectedAccount={null} 
                            onSave={() => { 
                                setShowFactureModal(false); 
                                setRefreshKey(prev => prev + 1); // Rafraîchit après modification
                            }}
                            onCancel={() => setShowFactureModal(false)}
                            refreshDashboardData={() => setRefreshKey(prev => prev + 1)}
                            refreshAllData={refreshAllData}
                        />
                    )}

                    {/* Modal de Remboursement */}
                    {showReimbursementModal && (
                        <ReimbursementModal
                            show={showReimbursementModal}
                            debtCreditItems={selectedItems}
                            totalAmount={totalAmountToReimburse}
                            currentUser={currentUser}
                            persons={persons}
                            accounts={accounts}
                            onClose={handleCloseReimbursementModal}
                            selectedPerson={personFocusObject}
                            selectedAccount={globalSelectedAccount}
                            themeColors={themeColors}
                        />
                    )}
                </>
            ) : (
                <p style={{ textAlign: 'center', marginTop: '50px', color: themeColors.text }}>Veuillez sélectionner une personne pour gérer les dettes et créances.</p>
            )}
        </div>
    );
}

// Composant pour l'en-tête de tableau réutilisable
function DebtCreditTableHeader({ themeColors }) {
    const headerItems = [
        ['Sélection', 'center', '50px'],
        ['Description Facture', 'left'],
        ['Partenaire', 'left'],
        ['Dû le', 'center'],
        ['Montant Initial', 'center'],
        ['Reste à Payer', 'center'],
        ['Actions', 'center'],
    ];

    return (
        <tr style={{ backgroundColor: themeColors.background }}>
            {headerItems.map(([header, align, width], index) => (
                <th
                    key={index}
                    style={{
                        padding: '10px',
                        borderBottom: `2px solid ${themeColors.secondary}`,
                        textAlign: align,
                        backgroundColor: themeColors.navActive,
                        color: themeColors.text,
                        width: width || 'auto'
                    }}
                >
                    {header}
                </th>
            ))}
        </tr>
    );
}